
    // IMPORTS
    import { computed, defineComponent, onMounted, ref } from 'vue'
    import router from '@/router'
    import { useRoute } from 'vue-router'

    import { StackLayout, Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import PortalPage from '@/components/common/portal-page.vue'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import MyGrid from '@/components/grid/my-grid.vue'

    import { GridColumnProps } from '@progress/kendo-vue-grid'
    import { SortDescriptor } from '@progress/kendo-data-query'
    import { buildMaterialLocalization, EquipmentApi } from '@/util'
    import { IBomItemSlim, IEquipmentDataWithBomItemWarnings } from '@/types/equipment'

    interface IWarningRow {
        materialNo: number,
        materialText: string,
        plannedQty: number,
        actualQty: number,
        warningText: string
    }

    export default defineComponent({
        name: 'adminEquipmentWarnings',

        components: {
            PortalPage,
            MyGrid,
            LoaderGuard,
            StackLayout,
            Card,
            CardHeader,
            CardBody,
            CardTitle
        },

        setup() {

            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const { localize, materialTextFor } = buildMaterialLocalization()

            // Get equipment number from parameterized URL segment 'eqNo'
            const route = useRoute()
            const equipmentNo = parseInt(route.params.eqNo as string)

            if (equipmentNo == 0 || isNaN(equipmentNo)) {
                router.go(-1)
            }

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const data = ref<IEquipmentDataWithBomItemWarnings | null>(null)
            const publicData = { data }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const pageTitle = computed(() => `${localize('warnings')}: ${data.value?.serialNo} ${data.value?.description}`)
            const warnings = computed(() => transform(data.value))
            const initialSort = computed<SortDescriptor[]>(() => [{ field: 'materialNo' }])
            const columns = computed(() => [
                {
                    field: 'materialNo',
                    title: localize('materialNo'),
                    width: 200
                },
                {
                    field: 'materialText',
                    title: localize('description')
                },
                {
                    field: 'plannedQty',
                    title: localize('plannedQty'),
                    type: 'numeric',
                    filter: 'numeric',
                    width: 200
                },
                {
                    field: 'actualQty',
                    title: localize('actualQty'),
                    type: 'numeric',
                    filter: 'numeric',
                    width: 200
                },
                {
                    field: 'warningText',
                    title: localize('warning'),
                    className: 'warning-text'
                }
            ] as GridColumnProps[])

            const computedData = { pageTitle, warnings, initialSort, columns }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const publicMethods = { localize }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function loadData() {
                const api = new EquipmentApi

                api.getEquipmentBomItemsWithWarnings(equipmentNo)
                    .then(response => data.value = response.data)
            }

            function transform(equipment: IEquipmentDataWithBomItemWarnings | null): IWarningRow[] {
                if (equipment == null) return []

                return equipment.bomItems.map(b => ({
                    materialNo: b.material.materialNo,
                    materialText: materialTextFor(b.material),
                    plannedQty: b.qty,
                    actualQty: b.actualQty,
                    warningText: getWarningText(b)
                }) as IWarningRow)
            }

            function getWarningText(bomItem: IBomItemSlim): string {
                if (bomItem.qty != bomItem.actualQty) {
                    if (bomItem.actualQty == 0) {
                        return localize('warningNoActualQty')
                    }

                    if (bomItem.qty == 0) {
                        return localize('warningNoPlannedQty')
                    }

                    return localize('warningQtyDifference')
                }

                return ''
            }

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(() => loadData())

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
